import type { Pool } from "@aetherdex/shared"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { TokenChip } from "../components/TokenChip"
import { Card, CardBody } from "../components/ui/Card"
import { Input } from "../components/ui/Input"
import { PageHeader } from "../components/ui/PageHeader"
import { Stat } from "../components/ui/Stat"
import { poolsQueryOptions } from "../lib/pools-query"

interface Token {
  address: string
  symbol: string
  name: string
  decimals: number
  logoURI?: string
}

const SORT_OPTIONS = [
  { value: "tvl", label: "TVL" },
  { value: "volume", label: "Volume 24h" },
  { value: "fees", label: "Fees 24h" },
] as const

type SortBy = (typeof SORT_OPTIONS)[number]["value"]

const isSortBy = (value: unknown): value is SortBy => SORT_OPTIONS.some((option) => option.value === value)

export const Route = createFileRoute("/pools")({
  component: PoolsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    sortBy: isSortBy(search.sortBy) ? search.sortBy : "tvl",
    filterToken: (search.filterToken as string) ?? "",
  }),
})

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1"

function PoolsPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  if (pathname !== "/pools") return <Outlet />

  return <PoolsIndexPage />
}

function PoolsIndexPage() {
  const { sortBy, filterToken } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [search, setSearch] = useState(filterToken)

  // Keep the input in sync when the URL filter changes (e.g. header navigation).
  useEffect(() => {
    setSearch(filterToken)
  }, [filterToken])

  const { data, isPending, isError, refetch } = useQuery(
    poolsQueryOptions(50, 0, {
      sortBy,
      sortDirection: "desc",
      filterToken: filterToken === "" ? undefined : filterToken,
    }),
  )
  const pools = data?.pools ?? []

  const { data: tokenList } = useQuery({
    queryKey: ["tokens", "verified"],
    queryFn: async ({ signal }) => {
      const res = await fetch(`${API_URL}/tokens?verified=true&limit=200`, { signal })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = (await res.json()) as { tokens: Token[] }
      return json.tokens ?? []
    },
    staleTime: 60_000,
  })
  const tokens = useMemo(() => {
    const map: Record<string, Token> = {}
    for (const t of tokenList ?? []) map[t.address.toLowerCase()] = t
    return map
  }, [tokenList])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate({ search: { sortBy, filterToken: search } })
  }

  return (
    <div className="mx-auto max-w-6xl py-8">
      <PageHeader
        title="Pools"
        eyebrow="Concentrated liquidity"
        description="Browse the active Uniswap V3 + V4 pools on Aether."
        actions={
          <Link to="/pools/new" search={{ sortBy: "tvl", filterToken: "" }} className="btn btn-primary btn-sm">
            New Position
          </Link>
        }
      />

      <div className="mb-6 flex flex-wrap items-end gap-4">
        <form onSubmit={handleSearch} className="max-w-sm flex-1">
          <Input
            label="Filter by token"
            placeholder="0x… or symbol"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>
        <fieldset className="flex flex-col gap-1">
          <legend className="label-text text-xs">Sort by</legend>
          <div className="flex flex-wrap gap-1" id="sort-select">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => navigate({ search: { sortBy: opt.value, filterToken } })}
                className={`btn btn-sm ${sortBy === opt.value ? "btn-primary" : "btn-ghost border hairline"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      <PoolsResults isPending={isPending} isError={isError} pools={pools} tokens={tokens} onRetry={() => refetch()} />
    </div>
  )
}

interface PoolsResultsProps {
  isPending: boolean
  isError: boolean
  pools: readonly Pool[]
  tokens: Record<string, Token>
  onRetry: () => void
}

export function PoolsResults({ isPending, isError, pools, tokens, onRetry }: PoolsResultsProps) {
  if (isPending) {
    return (
      <div className="flex items-center justify-center gap-3 py-12" role="status" aria-label="Loading pools">
        <span className="loading loading-spinner loading-md text-primary" aria-hidden="true" />
        <span className="text-sm text-base-content/60">Loading pools…</span>
      </div>
    )
  }

  if (isError) {
    return (
      <Card>
        <CardBody>
          <p className="text-center font-medium">Failed to load pools.</p>
          <p className="mt-1 text-center text-sm text-base-content/60">
            Pool data is temporarily unavailable — please retry.
          </p>
          <div className="mt-4 flex justify-center">
            <button type="button" onClick={onRetry} className="btn btn-primary btn-sm">
              Retry
            </button>
          </div>
        </CardBody>
      </Card>
    )
  }

  if (pools.length === 0) {
    return (
      <Card>
        <CardBody>
          <p className="py-8 text-center text-base-content/60">No pools found.</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {pools.map((pool) => {
        const t0 = tokens[pool.token0Address.toLowerCase()]
        const t1 = tokens[pool.token1Address.toLowerCase()]
        return (
          <Link
            key={pool.poolId}
            to="/pools/$poolId"
            params={{ poolId: pool.poolId }}
            search={{ sortBy: "tvl", filterToken: "" }}
            className="block"
          >
            <Card className="transition-colors hover:border-primary">
              <CardBody>
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {t0 ? <TokenChip token={t0} /> : null}
                    <span className="text-base-content/40">/</span>
                    {t1 ? <TokenChip token={t1} /> : null}
                  </div>
                  <span className="badge badge-ghost">{(pool.fee / 10_000).toFixed(2)}%</span>
                </div>
                <div className="stats stats-horizontal bg-transparent">
                  <Stat label="TVL" value={`$${formatUsd(pool.tvlUsd)}`} />
                  <Stat label="Vol 24h" value={`$${formatUsd(pool.volume24hUsd)}`} />
                  <Stat label="Fees 24h" value={`$${formatUsd(pool.fees24hUsd)}`} />
                </div>
              </CardBody>
            </Card>
          </Link>
        )
      })}
    </div>
  )
}

function formatUsd(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)}K`
  if (value >= 1) return value.toFixed(2)
  if (value > 0) return value.toFixed(4)
  return "0.00"
}
